import React from 'react';
import { styled } from '@mui/material';
import InsideLoading from 'components/loading/InsideLoading';
import NoData from 'components/no-data/NoData';
import { plural, RecordMetric, RecordMetrics } from 'components/record-ficha/RecordMetric';
import RecordPriceList from 'components/record-ficha/RecordPriceList';
import RecordTimeline from 'components/record-ficha/RecordTimeline';
import { moneyFormat } from 'helpers/moneyFormat';
import { useFetchHospitalRecord } from './hooks/useFetchHospitalRecord';

const Content = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  minWidth: 0,
}));

interface HospitalRecordContentProps {
  id: string | undefined;
  reloadKey?: number;
}

const HospitalRecordContent: React.FC<HospitalRecordContentProps> = ({ id, reloadKey }) => {
  const { record, loading, error } = useFetchHospitalRecord(id, reloadKey);

  if (loading) return <InsideLoading />;

  if (error || !record) return <NoData message="Não foi possível carregar a ficha do hospital." />;

  const { hospital, budgets } = record;
  const closed = budgets.summary.closed;
  const open = budgets.summary.by_status?.open;

  return (
    <Content>
      <RecordMetrics sx={{ gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
        <RecordMetric
          label="Orçamentos fechados"
          value={moneyFormat(closed.value_total)}
          helpers={[plural(closed.count, 'fechado', 'fechados')]}
        />
        <RecordMetric
          label="Orçamentos em aberto"
          value={moneyFormat(open?.value_total ?? 0)}
          helpers={[plural(open?.count ?? 0, 'orçamento', 'orçamentos')]}
        />
      </RecordMetrics>

      <RecordPriceList
        title="Procedimentos atendidos"
        items={hospital.procedures.map(item => ({
          key: item.id,
          name: item.procedure?.name || '-',
          min: item.sale_price_min,
          max: item.sale_price_max,
        }))}
        empty="Este hospital ainda não tem procedimentos cadastrados. Adicione na aba Procedimentos."
        note="Os preços são editados na aba Procedimentos."
      />

      <RecordTimeline
        budgets={budgets.items}
        guides={[]}
        counterpart="patient"
        subject="Este hospital"
        showFilters={false}
      />
    </Content>
  );
};

export default HospitalRecordContent;
